import { useState, useEffect, useRef } from "react";
import html from "../../images/html.png";
import css from "../../images/css.png";
import js from "../../images/js.png";
import tailwind from "../../images/tailwind.png";
import python from "../../images/python.png";
import django from "../../images/django.png";
import git from "../../images/git.png";
import react from "../../images/react.png";
import mysql from "../../images/mysql.png";
import materialUI from "../../images/materialUI.png";
import bootstrap from "../../images/bootstrap.png";
import postman from "../../images/postman.png";
import jira from "../../images/jira.svg";

const postgresql = "/assets/postgresql.png";

const langColors = {
  "React.js":    "#61dafb",
  "JavaScript":  "#f1e05a",
  "HTML":        "#e34c26",
  "CSS":         "#563d7c",
  "Tailwind CSS":"#06b6d4",
  "Material UI": "#007fff",
  "Bootstrap":   "#7952b3",
  "Python":      "#3572A5",
  "Django":      "#44b78b",
  "MySQL":       "#4479A1",
  "PostgreSQL":  "#336791",
  "Git/GitHub":  "#f0f6fc",
  "Postman":     "#FF6C37",
  "JIRA":        "#0052CC",
};

const skillCategories = [
  {
    title: "Frontend Development",
    description: "Building responsive, polished interfaces with React ecosystem",
    icon: "⚛️",
    color: "#61dafb",
    skills: [
      { name: "React.js", icon: react, level: 90 },
      { name: "JavaScript", icon: js, level: 85 },
      { name: "HTML", icon: html, level: 95 },
      { name: "CSS", icon: css, level: 90 },
      { name: "Tailwind CSS", icon: tailwind, level: 85 },
      { name: "Material UI", icon: materialUI, level: 80 },
      { name: "Bootstrap", icon: bootstrap, level: 75 },
    ],
  },
  {
    title: "Backend Development",
    description: "Python, Django, APIs, and database-backed application flows",
    icon: "🐍",
    color: "#44b78b",
    skills: [
      { name: "Python", icon: python, level: 75 },
      { name: "Django", icon: django, level: 70 },
      { name: "MySQL", icon: mysql, level: 65 },
      { name: "PostgreSQL", icon: postgresql, level: 65 },
      { name: "Postman", icon: postman, level: 80 },
    ],
  },
  {
    title: "Tools & Workflow",
    description: "Development tools and project management",
    icon: "🔧",
    color: "#f0f6fc",
    skills: [
      { name: "Git/GitHub", icon: git, level: 85 },
      { name: "JIRA", icon: jira, level: 75 },
    ],
  },
];

const softSkills = [
  "Responsive Design", "API Integration", "Version Control",
  "Debugging", "Code Review", "Agile / Scrum",
  "UI/UX Design", "Problem Solving", "REST APIs", "Database Design",
  "Cross-browser Testing", "Performance Optimization",
];

const getLevel = (n) => {
  if (n >= 90) return "Expert";
  if (n >= 80) return "Advanced";
  if (n >= 70) return "Intermediate";
  return "Beginner";
};

const getLevelColor = (n) => {
  if (n >= 90) return "var(--gh-green)";
  if (n >= 80) return "var(--gh-link)";
  if (n >= 70) return "var(--gh-yellow)";
  return "var(--gh-text-secondary)";
};

// Animated bar that fills when it enters viewport
const AnimatedBar = ({ level, color }) => {
  const [width, setWidth] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setWidth(level); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [level]);

  return (
    <div ref={ref} style={{ height: 6, background: "var(--gh-card)", borderRadius: 3, overflow: "hidden" }}>
      <div style={{
        height: "100%",
        width: `${width}%`,
        background: color,
        borderRadius: 3,
        transition: "width 1s cubic-bezier(0.4,0,0.2,1)",
      }} />
    </div>
  );
};

// Language bar breakdown (like GitHub)
const LanguageBreakdown = () => {
  const langs = [
    { name: "React.js / JSX", color: "#61dafb", pct: 42 },
    { name: "JavaScript",     color: "#f1e05a", pct: 18 },
    { name: "Python",         color: "#3572A5", pct: 15 },
    { name: "Django",         color: "#44b78b", pct: 10 },
    { name: "CSS / Tailwind", color: "#06b6d4", pct: 10 },
    { name: "Other",          color: "var(--gh-text-secondary)", pct: 5  },
  ];

  return (
    <div style={{ background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6, padding: 20, marginBottom: 24 }}>
      <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--gh-text)", marginBottom: 16 }}>
        Languages & Frameworks
      </h3>

      {/* Stacked bar */}
      <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", marginBottom: 16 }}>
        {langs.map(l => (
          <div
            key={l.name}
            style={{ width: `${l.pct}%`, background: l.color }}
            title={`${l.name}: ${l.pct}%`}
          />
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 20px" }}>
        {langs.map(l => (
          <span key={l.name} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--gh-text-secondary)" }}>
            <span style={{ width: 10, height: 10, borderRadius: "50%", background: l.color, display: "inline-block" }} />
            <span style={{ color: "var(--gh-text)" }}>{l.name}</span>
            <span>{l.pct}%</span>
          </span>
        ))}
      </div>
    </div>
  );
};

const Skills = () => {
  const [activeTab, setActiveTab] = useState("overview");

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "frontend", label: "Frontend" },
    { id: "backend", label: "Backend" },
    { id: "tools", label: "Tools" },
  ];

  const allSkills = skillCategories.flatMap(c => c.skills);

  return (
    <div>
      {/* Page header */}
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: "var(--gh-text)", marginBottom: 4 }}>Skills</h2>
        <p style={{ fontSize: 14, color: "var(--gh-text-secondary)" }}>
          Full stack technical expertise across frontend, backend, APIs, databases, and workflow tools.
        </p>
      </div>

      {/* Language breakdown chart */}
      <LanguageBreakdown />

      {/* Tabs */}
      <div style={{ borderBottom: "1px solid var(--gh-border)", marginBottom: 24 }}>
        <nav style={{ display: "flex", gap: 0 }}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`gh-tab${activeTab === tab.id ? " active" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview tab: all skills as cards */}
      {activeTab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          {skillCategories.map(cat => (
            <div key={cat.title} style={{ background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6, overflow: "hidden" }}>
              {/* Category header */}
              <div style={{ padding: "12px 20px", background: "var(--gh-card)", borderBottom: "1px solid var(--gh-border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 18 }}>{cat.icon}</span>
                  <div>
                    <span style={{ fontSize: 14, fontWeight: 600, color: "var(--gh-text)" }}>{cat.title}</span>
                    <span style={{ fontSize: 12, color: "var(--gh-text-secondary)", marginLeft: 8 }}>{cat.description}</span>
                  </div>
                </div>
                <span className="gh-tab-count">{cat.skills.length} skills</span>
              </div>

              {/* Skills grid */}
              <div style={{ padding: 16, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
                {cat.skills.map(skill => (
                  <div key={skill.name} style={{
                    background: "var(--gh-card)", border: "1px solid var(--gh-border)", borderRadius: 6, padding: "12px 14px",
                    transition: "border-color 0.15s",
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "var(--gh-link)"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "var(--gh-border)"}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                      <div style={{ width: 32, height: 32, background: "var(--gh-border)", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <img src={skill.icon} alt={skill.name} style={{ width: 20, height: 20, objectFit: "contain" }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--gh-text)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{skill.name}</span>
                          <span style={{ fontSize: 11, color: getLevelColor(skill.level), marginLeft: 6, flexShrink: 0 }}>
                            {getLevel(skill.level)}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "var(--gh-text-secondary)", display: "flex", alignItems: "center", gap: 4 }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: langColors[skill.name] || "var(--gh-text-secondary)", display: "inline-block" }} />
                          {skill.level}%
                        </div>
                      </div>
                    </div>
                    <AnimatedBar level={skill.level} color={langColors[skill.name] || "var(--gh-link)"} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Frontend tab */}
      {activeTab === "frontend" && (
        <div style={{ background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6, overflow: "hidden" }}>
          <div style={{ padding: "12px 20px", background: "var(--gh-card)", borderBottom: "1px solid var(--gh-border)" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--gh-text)" }}>Frontend Skills Breakdown</span>
          </div>
          <div style={{ padding: 20 }}>
            {skillCategories[0].skills.map(skill => (
              <div key={skill.name} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <img src={skill.icon} alt={skill.name} style={{ width: 18, height: 18, objectFit: "contain" }} />
                    <span style={{ fontSize: 14, color: "var(--gh-text)" }}>{skill.name}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: getLevelColor(skill.level) }}>{getLevel(skill.level)}</span>
                    <span style={{ fontSize: 12, color: "var(--gh-text-secondary)" }}>{skill.level}%</span>
                  </div>
                </div>
                <AnimatedBar level={skill.level} color={langColors[skill.name] || "var(--gh-link)"} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Backend tab */}
      {activeTab === "backend" && (
        <div style={{ background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6, overflow: "hidden" }}>
          <div style={{ padding: "12px 20px", background: "var(--gh-card)", borderBottom: "1px solid var(--gh-border)" }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: "var(--gh-text)" }}>Backend Skills Breakdown</span>
          </div>
          <div style={{ padding: 20 }}>
            {skillCategories[1].skills.map(skill => (
              <div key={skill.name} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <img src={skill.icon} alt={skill.name} style={{ width: 18, height: 18, objectFit: "contain" }} />
                    <span style={{ fontSize: 14, color: "var(--gh-text)" }}>{skill.name}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 12, color: getLevelColor(skill.level) }}>{getLevel(skill.level)}</span>
                    <span style={{ fontSize: 12, color: "var(--gh-text-secondary)" }}>{skill.level}%</span>
                  </div>
                </div>
                <AnimatedBar level={skill.level} color={langColors[skill.name] || "var(--gh-link)"} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tools tab */}
      {activeTab === "tools" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {[skillCategories[2]].map(cat => (
            <div key={cat.title} style={{ background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6, overflow: "hidden" }}>
              <div style={{ padding: "12px 20px", background: "var(--gh-card)", borderBottom: "1px solid var(--gh-border)" }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: "var(--gh-text)" }}>{cat.icon} {cat.title}</span>
              </div>
              <div style={{ padding: 20 }}>
                {cat.skills.map(skill => (
                  <div key={skill.name} style={{ marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <img src={skill.icon} alt={skill.name} style={{ width: 18, height: 18, objectFit: "contain" }} />
                        <span style={{ fontSize: 14, color: "var(--gh-text)" }}>{skill.name}</span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, color: getLevelColor(skill.level) }}>{getLevel(skill.level)}</span>
                        <span style={{ fontSize: 12, color: "var(--gh-text-secondary)" }}>{skill.level}%</span>
                      </div>
                    </div>
                    <AnimatedBar level={skill.level} color={langColors[skill.name] || "var(--gh-link)"} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Soft skills / topics */}
      <div style={{ marginTop: 24, background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6, padding: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: "var(--gh-text)", marginBottom: 14 }}>
          Additional Skills & Practices
        </h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {softSkills.map(s => (
            <span key={s} className="gh-topic">{s}</span>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <div style={{ marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 12 }}>
        {[
          { label: "Total Skills", value: allSkills.length, color: "var(--gh-link)" },
          { label: "Expert Level", value: allSkills.filter(s => s.level >= 90).length, color: "var(--gh-green)" },
          { label: "Advanced", value: allSkills.filter(s => s.level >= 80 && s.level < 90).length, color: "#61dafb" },
          { label: "Years Coding", value: "2.5+", color: "var(--gh-purple)" },
        ].map(stat => (
          <div key={stat.label} style={{
            background: "var(--gh-surface)", border: "1px solid var(--gh-border)", borderRadius: 6,
            padding: "16px", textAlign: "center",
          }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: stat.color }}>{stat.value}</div>
            <div style={{ fontSize: 12, color: "var(--gh-text-secondary)", marginTop: 4 }}>{stat.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Skills;
